/**
 * Formats duration in seconds to a human-readable timestamp (e.g. "4:12" or "1:15:30").
 */
export const formatDuration = (seconds?: number | null): string | null => {
    if (seconds == null || isNaN(seconds) || seconds <= 0) {
        return null;
    }

    const totalSeconds = Math.round(seconds);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    const paddedSecs = secs.toString().padStart(2, '0');

    if (hours > 0) {
        const paddedMins = minutes.toString().padStart(2, '0');
        return `${hours}:${paddedMins}:${paddedSecs}`;
    }

    return `${minutes}:${paddedSecs}`;
};

/**
 * Formats a date as a human-readable relative time (e.g. "5 minutes ago").
 * Falls back to an absolute date for anything a week or older.
 */
export const formatRelativeTime = (date?: Date | string | null, now: Date = new Date()): string | null => {
    if (!date) {
        return null;
    }

    const value = date instanceof Date ? date : new Date(date);
    if (isNaN(value.getTime())) {
        return null;
    }

    const diffSeconds = Math.floor((now.getTime() - value.getTime()) / 1000);

    if (diffSeconds < 0) {
        return value.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
    }

    if (diffSeconds < 60) {
        return 'just now';
    }

    const diffMinutes = Math.floor(diffSeconds / 60);
    if (diffMinutes < 60) {
        return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
    }

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) {
        return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    }

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) {
        return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    }

    return value.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};
